// ===========================================
// Payments Page
// ===========================================
// Admin page for managing bank details, payment plans, invoice settings and discounts

import { useState } from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import {
  AccountBalance as BankIcon,
  CalendarMonth as PlansIcon,
  CreditCard as PaymentIcon,
  Discount as DiscountIcon,
  Receipt as InvoiceIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../hooks/usePermissions';
import PageHeader from '../../components/common/PageHeader';
import BankDetailsTab from '../../components/admin/pricing/BankDetailsTab';
import PaymentPlansTab from '../../components/admin/pricing/PaymentPlansTab';
import PaymentSettingsTab from '../../components/admin/settings/PaymentSettingsTab';
import DiscountSettingsTab from '../../components/admin/settings/DiscountSettingsTab';
import InvoiceSettingsTab from '../../components/admin/settings/InvoiceSettingsTab';

// ===========================================
// TYPES
// ===========================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ===========================================
// TAB PANEL COMPONENT
// ===========================================

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payments-tabpanel-${index}`}
      aria-labelledby={`payments-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `payments-tab-${index}`,
    'aria-controls': `payments-tabpanel-${index}`,
  };
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function PricingPage() {
  const [activeTab, setActiveTab] = useState(0);
  const { can } = usePermissions();
  // Roles slice 1a (V7b): the page and the API agree. The Invoice Settings API needs
  // operations_invoices; the Payment Settings API needs operations_payments.
  // Both APIs also sit behind the settings router's `system_settings.read` (#360).
  const canSeeInvoiceSettings = can('operations_invoices', 'read') && can('system_settings', 'read');
  const canSeePaymentSettings = can('operations_payments', 'read') && can('system_settings', 'read');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <PageHeader
        title="Payments"
        subtitle="Manage bank details, payment plans, invoice settings and discounts"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Payments' },
        ]}
      />

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<BankIcon />}
            iconPosition="start"
            label="Bank Details"
            value={0}
            {...a11yProps(0)}
          />
          <Tab
            icon={<PlansIcon />}
            iconPosition="start"
            label="Payment Plans"
            value={1}
            {...a11yProps(1)}
          />
          {canSeePaymentSettings && (
            <Tab
              icon={<PaymentIcon />}
              iconPosition="start"
              label="Payment Settings"
              value={2}
              {...a11yProps(2)}
            />
          )}
          {canSeeInvoiceSettings && (
            <Tab
              icon={<InvoiceIcon />}
              iconPosition="start"
              label="Invoice Settings"
              value={3}
              {...a11yProps(3)}
            />
          )}
          <Tab
            icon={<DiscountIcon />}
            iconPosition="start"
            label="Discounts"
            value={4}
            {...a11yProps(4)}
          />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <BankDetailsTab />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <PaymentPlansTab />
      </TabPanel>

      {canSeePaymentSettings && (
        <TabPanel value={activeTab} index={2}>
          <PaymentSettingsTab />
        </TabPanel>
      )}

      {canSeeInvoiceSettings && (
        <TabPanel value={activeTab} index={3}>
          <InvoiceSettingsTab />
        </TabPanel>
      )}

      <TabPanel value={activeTab} index={4}>
        <DiscountSettingsTab />
      </TabPanel>
    </Box>
  );
}
